import type { DictionaryWord } from '../dictionary-words-modal/dictionary-words-modal';

import {
  GhostButton,
  IconButton,
  Modal,
  Paragraph,
  PencilIcon,
  PrimaryButton,
  Tooltip,
  TrashIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from '../../../../components/search';

import styles from './dictionary-words-list.module.css';

type DictionaryWordsListProps = {
  dictionaryWords: DictionaryWord[];
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onRemoveAll: () => void;
};

function formatWordDisplay({ word, soundsLike }: DictionaryWord): string {
  if (soundsLike.length > 0) {
    return `${word}; ${soundsLike.join(', ')}`;
  }

  return word;
}

export function DictionaryWordsList({ dictionaryWords, onEdit, onRemove, onRemoveAll }: DictionaryWordsListProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

  useEffect(() => {
    setSearch('');
  }, [dictionaryWords.length]);

  if (dictionaryWords.length === 0) return null;

  const filteredWords = dictionaryWords
    .map((dictionaryWord, index) => ({ ...dictionaryWord, originalIndex: index }))
    .filter(
      ({ word, soundsLike }) =>
        word.toLowerCase().includes(search.toLowerCase()) ||
        soundsLike.some((s) => s.toLowerCase().includes(search.toLowerCase()))
    );

  return (
    <div data-testid="dictionary-words-list" className={styles.wrapper}>
      <header>
        <span data-testid="dictionary-words-list-length">
          {t('manager.webcastSetup.language.inputs.customDictionary.list.title', { count: dictionaryWords.length })}
        </span>

        <GhostButton
          data-testid="dictionary-words-list-delete-all-button"
          small
          onClick={() => setDeleteConfirmationVisible(true)}
        >
          {t('manager.webcastSetup.language.inputs.customDictionary.list.deleteAll')}
        </GhostButton>
      </header>

      <Search
        placeholder={t('manager.webcastSetup.language.inputs.customDictionary.list.searchPlaceholder')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch('')}
      />

      <div data-testid="dictionary-words-list-items" className={styles.items}>
        {filteredWords.map((word) => (
          <div
            key={word.originalIndex}
            data-testid={`dictionary-word-item-${word.originalIndex}`}
            className={styles.item}
          >
            <span>{formatWordDisplay(word)}</span>

            <div className={styles.itemActions}>
              <Tooltip label={t('manager.webcastSetup.language.inputs.customDictionary.list.editWord')}>
                <IconButton
                  data-testid={`dictionary-word-item-${word.originalIndex}-edit-button`}
                  type="button"
                  aria-label={t('manager.webcastSetup.language.inputs.customDictionary.list.editWord')}
                  rounded
                  onClick={() => onEdit(word.originalIndex)}
                >
                  <PencilIcon />
                </IconButton>
              </Tooltip>

              <Tooltip label={t('manager.webcastSetup.language.inputs.customDictionary.list.removeWord')}>
                <IconButton
                  data-testid={`dictionary-word-item-${word.originalIndex}-remove-button`}
                  type="button"
                  aria-label={t('manager.webcastSetup.language.inputs.customDictionary.list.removeWord')}
                  rounded
                  onClick={() => onRemove(word.originalIndex)}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ))}

        {filteredWords.length === 0 && (
          <Paragraph data-testid="dictionary-words-list-no-results">
            {t('manager.webcastSetup.language.inputs.customDictionary.list.noWordsFound')}
          </Paragraph>
        )}
      </div>

      <Modal
        data-testid="dictionary-delete-all-confirmation-modal"
        title={t('manager.webcastSetup.language.inputs.customDictionary.list.deleteConfirmation.title')}
        isOpen={deleteConfirmationVisible}
        onClose={() => setDeleteConfirmationVisible(false)}
        footer={
          <div className={styles.deleteConfirmationModalFooter}>
            <GhostButton
              data-testid="dictionary-delete-all-cancel-button"
              small
              onClick={() => setDeleteConfirmationVisible(false)}
            >
              {t('manager.webcastSetup.language.inputs.customDictionary.list.deleteConfirmation.cancel')}
            </GhostButton>

            <PrimaryButton
              data-testid="dictionary-delete-all-confirm-button"
              small
              onClick={() => {
                setDeleteConfirmationVisible(false);
                onRemoveAll();
              }}
            >
              {t('manager.webcastSetup.language.inputs.customDictionary.list.deleteConfirmation.confirm')}
            </PrimaryButton>
          </div>
        }
      >
        <Paragraph>
          {t('manager.webcastSetup.language.inputs.customDictionary.list.deleteConfirmation.description')}
        </Paragraph>
      </Modal>
    </div>
  );
}
